import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Landmark } from "lucide-react";
import { getApiErrorMessage } from "@/lib/api";
import { EmailOtpStep } from "@/components/EmailOtpStep";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [setupToken, setSetupToken] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [challenge, setChallenge] = useState(null);
  const { login, completeSetup, verifyTwoFactor, resendTwoFactor } = useAdminAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.password_setup_required) {
        setSetupToken(data.setup_token);
        toast.info("First login — create your password now (valid 30 minutes).");
      } else if (data.two_factor_required) {
        setChallenge(data);
      } else {
        navigate("/admin", { replace: true });
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      const data = await completeSetup(setupToken, newPw);
      if (data?.two_factor_required) {
        setSetupToken(null); setChallenge(data);
        toast.success("Password created. Verify the code sent to your e-mail.");
      } else {
        toast.success("Password created — welcome!");
        navigate("/admin", { replace: true });
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err));
      if ((getApiErrorMessage(err) || "").includes("again")) setSetupToken(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="smifs-hero-gradient flex min-h-dvh items-center justify-center p-6">
      <div className="glass-card w-full max-w-md p-5 sm:p-8">
        <div className="mb-6 flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[var(--brand)]"><Landmark className="h-5 w-5 text-white" /></div>
          <div>
            <div className="font-heading text-lg text-[var(--brand)]">SMIFS Ops</div>
            <div className="text-[11px] text-[var(--ink-muted)]">Admin Dashboard</div>
          </div>
        </div>
        <h2 className="font-heading text-xl text-[var(--brand)]">{challenge ? "E-mail verification" : setupToken ? "Create your password" : "Admin sign in"}</h2>
        {challenge ? <EmailOtpStep challenge={challenge} onVerify={async (code) => { await verifyTwoFactor(challenge.challenge_id, code); toast.success("Sign-in verified"); navigate("/admin", { replace: true }); }} onResend={async () => { const next = await resendTwoFactor(challenge.challenge_id); setChallenge(next); return next; }} onCancel={() => { setChallenge(null); setPassword(""); }} title="Verify administrator sign-in" /> : setupToken ? (
          <form className="mt-6 space-y-4" onSubmit={handleSetup}>
            <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800" data-testid="admin-setup-notice">
              First login for <b>{email}</b>. Set a password (10+ characters, with upper, lower and a digit). This one-time link expires in 30 minutes.
            </p>
            <div className="space-y-1.5">
              <Label>New password</Label>
              <Input type="password" required value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="admin-setup-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Confirm password</Label>
              <Input type="password" required value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} data-testid="admin-setup-confirm-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" data-testid="admin-setup-submit-button">
              <ShieldCheck className="mr-2 h-4 w-4" /> {loading ? "Saving..." : "Create password & sign in"}
            </Button>
          </form>
        ) : (
        <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Password</Label>
            <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password-input" />
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" data-testid="admin-login-submit-button">
            <ShieldCheck className="mr-2 h-4 w-4" /> {loading ? "Signing in..." : "Sign in"}
          </Button>
        </form>
        )}
      </div>
    </div>
  );
}
